import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowRight, Check, Loader2, Pencil, Search } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/gestione/costi`;
const PAGINA = 30;
const slug = (s) => String(s || 'nessuno').toLowerCase().replace(/[^a-z0-9]+/g, '-');
const euro = (v) => `${Number(v).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const STATI = {
  sospetto: ['Abbinamento sospetto', 'bg-red-100 text-red-700'],
  senza_prodotto: ['Senza prodotto', 'bg-gray-200 text-gray-700'],
  senza_prezzo: ['Senza prezzo', 'bg-amber-100 text-amber-800'],
  ok: ['Probabile', 'bg-emerald-100 text-emerald-800'],
  confermato: ['Confermato', 'bg-blue-100 text-blue-800'],
};
const FILTRI = [['da_controllare', 'Da controllare'], ['sospetti', 'Sospetti'], ['tutti', 'Tutti']];

const prezzoTesto = (p) => {
  if (p?.prezzo_kg != null) return `${euro(p.prezzo_kg)}/${p.unita === 'l' ? 'l' : 'kg'}`;
  if (p?.prezzo_pezzo != null) return `${euro(p.prezzo_pezzo)} a pezzo`;
  return 'nessun prezzo';
};

const Risultato = ({ p, onScegli, occupato }) => (
  <li>
    <button type="button" disabled={occupato} onClick={() => onScegli(p.chiave)} data-testid={`scegli-opzione-${slug(p.chiave)}`}
      className="w-full text-left rounded-md bg-white border border-gray-200 px-2.5 py-2 hover:border-blue-400 transition-colors disabled:opacity-50">
      <span className="block text-sm font-medium text-gray-900">{p.descrizione}</span>
      <span className="block text-xs text-gray-500">{p.fornitore} · {prezzoTesto(p)}</span>
    </button>
  </li>
);

const ScegliProdotto = ({ id, headers, onScegli, onChiudi, occupato }) => {
  const [cerca, setCerca] = useState('');
  const [risultati, setRisultati] = useState([]);
  const [carico, setCarico] = useState(false);
  useEffect(() => {
    if (cerca.trim().length < 2) { setRisultati([]); return undefined; }
    const t = setTimeout(async () => {
      setCarico(true);
      try {
        const { data } = await axios.get(`${API}/prodotti`, { headers: headers(), params: { filtro: 'tutti', cerca, n: 8 } });
        setRisultati(data.prodotti || []);
      } catch (e) {
        toast.error('Ricerca non riuscita');
      } finally {
        setCarico(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [cerca, headers]);
  return (
    <div className="mt-3 rounded-lg bg-gray-50 p-3 space-y-2" data-testid={`scegli-${id}`}>
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input autoFocus value={cerca} onChange={(e) => setCerca(e.target.value)} placeholder="Cerca nelle fatture (es. mele golden)"
          className="pl-8 h-9" data-testid={`scegli-cerca-${id}`} />
      </div>
      {carico && <p className="flex items-center gap-1 text-xs text-gray-500"><Loader2 className="h-3 w-3 animate-spin" /> Cerco…</p>}
      <ul className="space-y-1">{risultati.map((p) => <Risultato key={p.chiave} p={p} onScegli={onScegli} occupato={occupato} />)}</ul>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={onChiudi} data-testid={`scegli-annulla-${id}`}>Annulla</Button>
        <Button size="sm" variant="ghost" className="text-red-600" disabled={occupato} onClick={() => onScegli(null)} data-testid={`scegli-nessuno-${id}`}>
          Nessun prodotto adatto
        </Button>
      </div>
    </div>
  );
};

const Descrizione = ({ g }) => (
  <div className="min-w-0">
    <p className="text-sm font-semibold text-gray-900 flex flex-wrap items-center gap-1.5">
      {g.nome}
      <ArrowRight className="h-3.5 w-3.5 text-gray-400" />
      <span className={g.prodotto_fattura ? 'text-gray-800' : 'text-gray-400 italic font-normal'}>{g.prodotto_fattura || 'nessun prodotto'}</span>
    </p>
    <p className="text-xs text-gray-500">{g.prezzo?.fornitore ? `${g.prezzo.fornitore} · ` : ''}{prezzoTesto(g.prezzo)}</p>
    <p className="text-xs text-gray-500">in {g.piatti.length} piatti: {g.piatti.slice(0, 3).join(', ')}{g.piatti.length > 3 ? '…' : ''}</p>
  </div>
);

const RigaAbbinamento = ({ g, headers, onCambia, occupato }) => {
  const [aperto, setAperto] = useState(false);
  const id = `${slug(g.chiave_nome)}--${slug(g.chiave_prodotto)}`;
  const [etichetta, colore] = STATI[g.stato] || STATI.ok;
  const scegli = async (nuovo) => { if (await onCambia(g, nuovo)) setAperto(false); };
  return (
    <li className="py-3" data-testid={`abbinamento-${id}`}>
      <div className="flex items-start justify-between gap-3">
        <Descrizione g={g} />
        <span className={`shrink-0 px-2 py-0.5 rounded-full text-[11px] font-medium ${colore}`} data-testid={`abbinamento-stato-${id}`}>{etichetta}</span>
      </div>
      <div className="flex flex-wrap gap-2 mt-2">
        {g.prodotto_fattura && g.stato !== 'confermato' && (
          <Button size="sm" variant="outline" className="h-8 border-emerald-300 text-emerald-700" disabled={occupato}
            onClick={() => onCambia(g, g.chiave_prodotto)} data-testid={`abbinamento-giusto-${id}`}>
            <Check className="h-3.5 w-3.5 mr-1" /> È giusto
          </Button>
        )}
        <Button size="sm" variant="outline" className="h-8" onClick={() => setAperto(!aperto)} data-testid={`abbinamento-cambia-${id}`}>
          <Pencil className="h-3.5 w-3.5 mr-1" /> {g.prodotto_fattura ? 'Cambia prodotto' : 'Scegli prodotto'}
        </Button>
      </div>
      {aperto && <ScegliProdotto id={id} headers={headers} onScegli={scegli} onChiudi={() => setAperto(false)} occupato={occupato} />}
    </li>
  );
};

const Filtri = ({ filtro, setFiltro, cerca, setCerca }) => (
  <div className="flex flex-wrap gap-2">
    {FILTRI.map(([id, nome]) => (
      <button key={id} type="button" onClick={() => setFiltro(id)} data-testid={`abbinamenti-filtro-${id}`}
        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filtro === id ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
        {nome}
      </button>
    ))}
    <div className="relative flex-1 min-w-[180px]">
      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
      <Input value={cerca} onChange={(e) => setCerca(e.target.value)} placeholder="Cerca ingrediente o prodotto…" className="pl-8 h-8" data-testid="abbinamenti-cerca" />
    </div>
  </div>
);

const riepilogoTesto = (r) => (r
  ? `${r.sospetto} sospetti · ${r.senza_prodotto} senza prodotto · ${r.senza_prezzo} senza prezzo · ${r.confermato} confermati`
  : 'Carico…');

export default function AbbinamentiIngredienti({ headers }) {
  const [filtro, setFiltro] = useState('da_controllare');
  const [cerca, setCerca] = useState('');
  const [dati, setDati] = useState({ gruppi: [], totale: 0, riepilogo: null });
  const [caricamento, setCaricamento] = useState(true);
  const [occupato, setOccupato] = useState(false);

  const carica = useCallback(async (da = 0) => {
    setCaricamento(true);
    try {
      const { data } = await axios.get(`${API}/abbinamenti`, { headers: headers(), params: { filtro, cerca, da, n: PAGINA } });
      setDati((prima) => ({ ...data, gruppi: da ? [...prima.gruppi, ...data.gruppi] : data.gruppi }));
    } catch (e) {
      toast.error('Abbinamenti non disponibili');
    } finally {
      setCaricamento(false);
    }
  }, [filtro, cerca, headers]);

  useEffect(() => { const t = setTimeout(() => carica(0), 300); return () => clearTimeout(t); }, [carica]);

  const cambia = async (g, nuovo) => {
    setOccupato(true);
    try {
      const { data } = await axios.put(`${API}/abbinamenti`, { nome: g.chiave_nome, prodotto_attuale: g.chiave_prodotto, nuovo }, { headers: headers() });
      toast.success(`${g.nome}: aggiornato in ${data.ingredienti_aggiornati} ${data.ingredienti_aggiornati === 1 ? 'piatto' : 'piatti'}`);
      await carica(0);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Operazione non riuscita');
      return false;
    } finally {
      setOccupato(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white border border-gray-200 p-4 space-y-3" data-testid="abbinamenti-ingredienti">
      <div>
        <h2 className="text-base font-semibold text-gray-900">Ingredienti e prodotti delle fatture</h2>
        <p className="text-xs text-gray-500" data-testid="abbinamenti-riepilogo">{riepilogoTesto(dati.riepilogo)} · la scelta vale per tutti i piatti e per i menu futuri</p>
      </div>
      <Filtri filtro={filtro} setFiltro={setFiltro} cerca={cerca} setCerca={setCerca} />
      {caricamento && !dati.gruppi.length ? (
        <p className="flex items-center gap-2 text-sm text-gray-500 py-6" data-testid="abbinamenti-caricamento"><Loader2 className="h-4 w-4 animate-spin" /> Controllo le ricette…</p>
      ) : (
        <ul className="divide-y divide-gray-100" data-testid="abbinamenti-elenco">
          {dati.gruppi.map((g) => (
            <RigaAbbinamento key={`${g.chiave_nome}|${g.chiave_prodotto}`} g={g} headers={headers} onCambia={cambia} occupato={occupato} />
          ))}
          {!dati.gruppi.length && <li className="py-6 text-sm text-gray-500" data-testid="abbinamenti-vuoto">Niente da controllare qui.</li>}
        </ul>
      )}
      {dati.gruppi.length < dati.totale && (
        <Button variant="ghost" className="w-full" onClick={() => carica(dati.gruppi.length)} disabled={caricamento} data-testid="abbinamenti-altri">
          Mostra altri ({dati.totale - dati.gruppi.length})
        </Button>
      )}
    </div>
  );
}
